import React, { FC, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { Box, Container, Typography, useTheme, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Configure, InstantSearch } from 'react-instantsearch';
import { Link, useLocation, useParams } from 'react-router-dom';

import VideoClipIcon from '~/assets/icons/VideoClipIcon';
import CustomBlocks from '~/blocks/CustomBlocks/CustomBlocks';
import NotFoundPage from '~/blocks/NotFoundPage/NotFoundPage';
import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import AnimatedSkeletonMultiline from '~/components/AnimatedSkeletonMultiline';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import HeaderWithNavigation from '~/components/HeaderWithNavigation/HeaderWithNavigation';
import Hero from '~/components/Hero/Hero';
import JSBlock from '~/components/JSBlock/JSBlock';
import RouteWithCode from '~/components/RouteWithCode/RouteWithCode';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import SideVideoCard from '~/components/SideVideoCard/SideVideoCard';
import Summary from '~/components/Summary/Summary';
import CategoryLabel from '~/components/UI/CategoryLabel/CategoryLabel';
import VideoPlayer from '~/components/VideoPlayer/VideoPlayer';
import { FILTERS, RM_PAGES } from '~/const';
import { useRmMetadata } from '~/context/RmContext';
import { useSsrContext } from '~/context/SsrContext';
import { GET_GLOSSARY_TERM } from '~/graphql/discover';
import useCustomBlocks from '~/hooks/useCustomBlocks';
import useTenantSearchFilter from '~/hooks/useTenantSearchFilter';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useWatchList from '~/hooks/useWatchList';
import RelatedTerms from '~/pages/DiscoverPage/components/RelatedTerms/RelatedTerms';
import {
  createDiscoverCategoryFilterUrl,
  getNavigationArray,
} from '~/pages/DiscoverPage/pages/TermPage/helpers';
import { HITS_TYPES } from '~/pages/const';
import { buildGlossaryRoute, DISCOVER_GLOSSARY_ROUTE, DiscoverRoutes } from '~/routes';
import { css } from '~/styled';
import {
  BIG_MARGIN_PX,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SKELETON_TEXT_HEIGHT_SMALL_PX,
  SKELETON_TEXT_HEIGHT_TINY_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';
import { LearnerInfo, Query, QueryGetGlossaryTermArgs } from '~/types';
import { generateSeoTitle } from '~/utils/generateSeoTitle';
import redirectToNewSlug from '~/utils/redirectToNewSlug';
import getCanonicalUrl from '~/utils/urlUtils/getCanonicalUrl';

const relatedItemsFilter = `${FILTERS.SearchType}:${HITS_TYPES.GLOSSARY}`;

interface BreadcrumbData {
  '@type': string;
  position: number;
  item: {
    '@id': string;
    name: string;
  };
}

const getBreadcrumbItemData = (item, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@id': `https://financeunlocked.com${item.url}`,
    name: item.title,
  },
});

const getArticleData = (
  headline = '',
  description = '',
  breadcrumbs: BreadcrumbData[] = [],
  keywords: string[] = [],
  image,
) => ({
  '@context': 'https://schema.org/',
  '@type': ['Article'],
  headline,
  description,
  ...(image && { image }),
  mainEntityOfPage: {
    '@type': ['WebPage'],
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs,
    },
  },
  keywords,
});

interface TermPageProps {
  algoliaIndexName: string;
  searchClient: any;
  userInfo?: LearnerInfo;
  tenant?: string;
}

const TermPage: FC<TermPageProps> = ({ algoliaIndexName, searchClient, userInfo, tenant }) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const { term } = useParams();
  const { pathname } = useLocation();

  const { tenantFilter } = useTenantSearchFilter();

  useRmMetadata({ page: RM_PAGES.TermPage });
  const { redirect } = useSsrContext();

  const { data, loading, called } = useQuery<
    Pick<Query, 'getGlossaryTerm'>,
    QueryGetGlossaryTermArgs
  >(GET_GLOSSARY_TERM, {
    variables: {
      slug: term || '',
    },
    skip: !term,
  });

  const { blocks } = useCustomBlocks(DiscoverRoutes.Glossary, { skip: !!userInfo });

  const glossaryTerm = data?.getGlossaryTerm;
  const glossaryVideo = glossaryTerm?.glossary_video;
  const glossaryVideosIds = glossaryTerm?.videos
    ?.map((video) => video?.id)
    .filter((item): item is string => Boolean(item));

  const watchlistById = useWatchList(glossaryVideosIds);

  const termCategory = glossaryTerm?.categories?.[0];
  const termCategoryUrl = createDiscoverCategoryFilterUrl(termCategory, false);
  const navigationArray = getNavigationArray(
    termCategory?.title || '',
    termCategoryUrl,
    glossaryTerm?.title || '',
  );

  const articleJsonData = useMemo(() => {
    const breadcrumbsData: BreadcrumbData[] = navigationArray?.map(getBreadcrumbItemData);

    const categoryTitles = glossaryTerm?.categories
      ?.map((item) => item?.title)
      .filter((item): item is string => Boolean(item));

    const articleData = getArticleData(
      glossaryTerm?.title,
      glossaryTerm?.definition,
      breadcrumbsData,
      categoryTitles,
      glossaryTerm?.seo_image,
    );

    return JSON.stringify(articleData);
  }, [glossaryTerm, navigationArray]);

  const scriptData = {
    customScriptType: 'application/ld+json',
    js_code: articleJsonData,
  };

  if (!glossaryTerm && !loading && called) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  if (glossaryTerm && term) {
    redirectToNewSlug(redirect, pathname, term, glossaryTerm?.slug);
  }

  const cannonicalUrl = getCanonicalUrl(
    buildGlossaryRoute(glossaryTerm?.slug),
    glossaryTerm?.tenants,
  );

  if (loading) {
    return (
      <>
        {glossaryTerm && (
          <SeoBlock
            data={{
              seo_title: glossaryTerm?.seo_name || generateSeoTitle(glossaryTerm?.title, tenant),
              seo_description: glossaryTerm?.seo_description || glossaryTerm?.definition || '',
              ...(glossaryTerm?.seo_image && { share_image: glossaryTerm.seo_image }),
            }}
            canonicalUrl={cannonicalUrl}
          />
        )}
        <Hero>
          <Container>
            <LoadingBreadcrumbs xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
            <LoadingTitle xs={SKELETON_TEXT_HEIGHT_PX} />
            <CategoriesWrapper>
              <LoadingCategories xs={SKELETON_TEXT_HEIGHT_PX} />
              <LoadingCategories xs={SKELETON_TEXT_HEIGHT_PX} />
            </CategoriesWrapper>
          </Container>
        </Hero>
        <Container>
          <Wrapper>
            <TermBlocks>
              <TermBlock className="termBlock">
                <LoadingSubtitle xs={SKELETON_TEXT_HEIGHT_PX} />
                <ClearBoth>
                  <TermBlockWrapWithPadding>
                    <TermBlockVideoWrap>
                      <TermBlockVideo>
                        <AnimatedSkeleton xs="100%" />
                      </TermBlockVideo>
                    </TermBlockVideoWrap>
                  </TermBlockWrapWithPadding>
                  <LoadingGlossaryDefinition
                    xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                    lines={5}
                    spacer={TINY_MARGIN_PX}
                  />
                </ClearBoth>
              </TermBlock>
              <LoadingSubtitle xs={SKELETON_TEXT_HEIGHT_PX} />
              <AnimatedSkeletonMultiline
                xs={SKELETON_TEXT_HEIGHT_SMALL_PX}
                lines={3}
                spacer={TINY_MARGIN_PX}
              />
            </TermBlocks>
            <SideBlocks>
              <LoadingSideBlockTitle xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
              <LoadingRelatedTermsWrapper>
                {new Array(5).fill(null).map((item, index) => (
                  <LoadingRelatedTerm key={index} xs={SKELETON_TEXT_HEIGHT_TINY_PX} />
                ))}
              </LoadingRelatedTermsWrapper>
              <LoadingSideBlockTitle xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />
              <LoadingRelatedVideoCard xs="250px" />
              <LoadingRelatedVideoCard xs="250px" />
            </SideBlocks>
          </Wrapper>
        </Container>
      </>
    );
  }

  return (
    <>
      <SeoBlock
        data={{
          seo_title: glossaryTerm?.seo_name || generateSeoTitle(glossaryTerm?.title, tenant),
          seo_description: glossaryTerm?.seo_description || glossaryTerm?.definition || '',
          ...(glossaryTerm?.seo_image && { share_image: glossaryTerm.seo_image }),
        }}
        canonicalUrl={cannonicalUrl}
      />
      <JSBlock data={scriptData} />
      <Hero alt={glossaryTerm?.title}>
        <Container>
          <Breadcrumbs data-testid="termBreadcrumbs" crumbs={navigationArray} />
          <HeaderWithNavigation
            titleCss={titleCss}
            title={glossaryTerm?.title || ''}
            url={DISCOVER_GLOSSARY_ROUTE}
          />
          <CategoriesWrapper>
            <StylesCategoryLabel
              name={t('page.discover.glossary')}
              bgColor={theme.palette.common.yellow}
              textColor={theme.palette.common.blueDark}
              Icon={Boolean(glossaryVideo) ? StyledVideoClipIcon : undefined}
              data-testid="glossaryLabel"
            />
            {glossaryTerm?.categories &&
              glossaryTerm?.categories.map((category) => {
                const categoryUrl = createDiscoverCategoryFilterUrl(category);
                return (
                  <StylesCategoryLabel
                    key={category?.id}
                    as={Link}
                    to={categoryUrl}
                    data-testid="categoryLabel"
                    name="categoryLabel"
                  >
                    <CategoryLabel name={category?.title || ''} withDots={false} />
                  </StylesCategoryLabel>
                );
              })}
          </CategoriesWrapper>
        </Container>
      </Hero>
      <Container>
        <Wrapper>
          <TermBlocks>
            <TermBlock className="termBlock">
              <Typography className="termBlockTitle" data-testid="glossarySubtitle">
                {glossaryTerm?.subtitle}
              </Typography>
              <ClearBoth>
                {!!glossaryVideo && (
                  <TermBlockWrapWithPadding>
                    <TermBlockVideoWrap>
                      <TermBlockVideo>
                        <StyledVideoPlayer
                          id={glossaryVideo?.id || ''}
                          title={glossaryVideo?.title}
                          verseId={glossaryVideo?.verse_video_id}
                          duration={glossaryVideo?.duration}
                          expertName={glossaryVideo?.user?.name}
                        />
                      </TermBlockVideo>
                    </TermBlockVideoWrap>
                  </TermBlockWrapWithPadding>
                )}
                <Typography data-testid="glossaryDefinition" className="termBlockText">
                  {glossaryTerm?.definition}
                </Typography>
              </ClearBoth>
            </TermBlock>
            {!!glossaryTerm?.content && (
              <Summary summary={glossaryTerm?.content} wrapperCss={summaryWrapperCss} />
            )}
          </TermBlocks>
          <SideBlocks>
            {/*TODO fix displaying title if no related terms*/}
            <SideBlockTitle data-testid="relatedTermsTitle" variant="h3">
              {t('page.discover.relatedTerms')}
            </SideBlockTitle>
            <InstantSearch searchClient={searchClient} indexName={algoliaIndexName}>
              <Configure
                hitsPerPage={5}
                similarQuery={glossaryTerm?.title || ''}
                filters={`(${tenantFilter}) AND (${relatedItemsFilter})`}
              />
              <RelatedTerms term={glossaryTerm} />
            </InstantSearch>
            {Boolean(glossaryTerm?.videos?.length) && (
              <>
                <SideBlockTitle data-testid="relatedVideosTitle" variant="h3">
                  {t('page.discover.relatedVideos', { title: glossaryTerm?.title })}
                </SideBlockTitle>
                {glossaryTerm?.videos?.slice(0, 2).map((video) => (
                  <SideVideoCard
                    data-testid="relatedVideoCard"
                    key={video?.id}
                    id={video?.id}
                    title={video?.title}
                    author={video?.user?.name}
                    duration={video?.duration}
                    categories={video?.categories}
                    previewUrl={video?.preview_url}
                    link={`/videos/${video?.id}`}
                    difficulty={video?.difficulty}
                    wrapperCss={sideVideoCardWrapperCss}
                    isInWatchlist={watchlistById[video?.id || '']}
                  />
                ))}
              </>
            )}
          </SideBlocks>
        </Wrapper>
      </Container>
      {blocks.length > 0 && <CustomBlocks blocks={blocks} />}
    </>
  );
};

export default TermPage;

const CategoriesWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  display: flex;
  align-items: center;
`;

const LoadingBreadcrumbs = styled(AnimatedSkeleton)`
  width: 90%;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 50%;
  }
`;

const LoadingTitle = styled(AnimatedSkeleton)`
  width: 80%;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 40%;
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;

const LoadingCategories = styled(AnimatedSkeleton)`
  margin-right: ${TINY_MARGIN_PX};

  &:first-child {
    width: 80px;
  }

  &:last-child {
    width: 140px;
  }
`;

const LoadingSubtitle = styled(AnimatedSkeleton)`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;

const LoadingSideBlockTitle = styled(AnimatedSkeleton)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
  width: 80%;
`;

const LoadingRelatedTermsWrapper = styled('div')`
  margin-bottom: ${LARGE_MARGIN_PX};
`;

const LoadingRelatedTerm = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};

  &:first-child {
    width: 70%;
  }

  &:nth-child(2) {
    width: 50%;
  }

  &:nth-child(3) {
    width: 90%;
  }

  &:nth-child(4) {
    width: 60%;
  }
`;

const LoadingRelatedVideoCard = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};
`;

const LoadingGlossaryDefinition = styled(AnimatedSkeletonMultiline)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 50%;
  }
`;

const titleCss = css`
  & {
    line-height: 1;
  }
`;
const sideVideoCardWrapperCss = ({ theme }) => css`
  max-width: 358px;

  ${theme.breakpoints.up('md')} {
    width: 358px;
  }
`;
const summaryWrapperCss = ({ theme }) => css`
  && {
    margin-bottom: ${SUBMIDDLE_MARGIN_PX};

    ${theme.breakpoints.up('md')} {
      margin-bottom: ${MIDDLE_MARGIN_PX};
    }

    & h1,
    & h2,
    & h3,
    & h4,
    & h5,
    & h6 {
      color: ${theme.palette.common.white};
      font-weight: 700;
      font-size: 18px;
      line-height: 1.4;
      border-bottom: 3px solid ${alpha(theme.palette.common.violet, 0.3)};
      padding-bottom: 4px;
      margin-bottom: ${SUBMIDDLE_MARGIN_PX};

      ${theme.breakpoints.up('md')} {
        font-size: 20px;
        margin-bottom: ${MIDDLE_MARGIN_PX};
      }
    }

    & p,
    & ol,
    & ul {
      white-space: pre-wrap;
      color: ${theme.palette.common.gray};
      font-size: 18px;
      line-height: 1.4;
      width: 100%;

      ${theme.breakpoints.up('md')} {
        font-size: 20px;
        line-height: 1.5;
      }
    }
  }
`;
const StylesCategoryLabel = styled(CategoryLabel, {
  shouldForwardProp: (prop) => prop !== 'as',
})`
  margin-right: ${TINY_MARGIN_PX};
  margin-top: ${TINY_MARGIN_PX};
`;
const StyledVideoClipIcon = styled(VideoClipIcon)`
  color: ${({ theme }) => theme.palette.common.blueDark};
`;

const Wrapper = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('lg')} {
    display: flex;
    justify-content: space-between;
  }
`;
const TermBlocks = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex: 2;
  }

  & .termBlock {
    margin-bottom: 24px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      margin-bottom: ${MIDDLE_MARGIN_PX};
    }
  }
`;
const TermBlock = styled(Box)`
  & .termBlockTitle,
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    color: ${({ theme }) => theme.palette.common.white};
    font-weight: 700;
    font-size: 18px;
    line-height: 1.4;
    border-bottom: 3px solid ${({ theme }) => alpha(theme.palette.common.violet, 0.3)};
    padding-bottom: 4px;
    margin-bottom: 24px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
      margin-bottom: ${MIDDLE_MARGIN_PX};
    }
  }

  & .termBlockText,
  & p,
  & ol,
  & ul {
    white-space: pre-wrap;
    color: ${({ theme }) => theme.palette.common.gray};
    font-size: 18px;
    line-height: 1.4;
    width: 100%;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
      line-height: 1.5;
    }
  }
`;
const ClearBoth = styled(Box)`
  &:after {
    content: '';
    clear: both;
    display: table;
  }
`;
const TermBlockWrapWithPadding = styled(Box)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    float: right;
    width: 50%;
    padding-left: 24px;
  }
`;
const TermBlockVideoWrap = styled(Box)`
  width: 100%;
  overflow: hidden;
  position: relative;
  padding-bottom: 56%;
  border-radius: 5px;
  margin-bottom: 24px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
    padding-bottom: 56%;
  }
`;
const TermBlockVideo = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
`;
const SideBlocks = styled(Box)`
  margin: ${BIG_MARGIN_PX} auto 0;
  min-width: 288px;
  max-width: 343px;

  &:empty {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    margin: 0 0 0 ${MIDDLE_MARGIN_PX};
    width: 358px;
  }
`;
const StyledVideoPlayer = styled(VideoPlayer)`
  width: 100%;
  height: 100%;
`;
const SideBlockTitle = styled(Typography)`
  && {
    font-size: 18px;
    line-height: 1.4;
    margin-bottom: 24px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 20px;
    }
  }
`;
